import React, { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { HiOutlineArrowLeft } from "react-icons/hi2";
import LoyaltyEmailLogin from "../../molecules/LoyaltyEmailLogin";
import SocialAuth from "./SocialAuth";
import { IResolveParams } from "reactjs-social-login";
import { useUserSocialLogin } from "../../../hooks/users";
import LmcUserViewForgotPassword from "./LmcUserViewForgotPassword";

interface LoyaltyLoginModalProps {
  entityIdentification: string;
  onLoginSuccess: () => void;
  onBack?: () => void;
}

const LoyaltyLoginModal: React.FC<LoyaltyLoginModalProps> = ({
  entityIdentification,
  onLoginSuccess,
  onBack,
}) => {
  const { t } = useTranslation("clientView");
  const [showForgotPassword, setShowForgotPassword] = useState(false);

  const { mutate: socialLogin } = useUserSocialLogin();

  const handleResolveSocialAuth = useCallback(
    (provider: IResolveParams["provider"], data: IResolveParams["data"]) => {
      const cpPayload = {
        access_token:
          provider === "google"
            ? data?.access_token
            : data?.authorization?.id_token,
        provider,
      };
      socialLogin(
        {
          entity_identification: entityIdentification,
          data: { type: "login", providerPayload: cpPayload },
        },
        {
          onSuccess: () => {
            onLoginSuccess?.();
          },
        }
      );
    },
    [entityIdentification, socialLogin]
  );

  const handleRejectSocialAuth = useCallback(
    (err: any) => {
      console.log(err);
    },
    [t]
  );

  if (showForgotPassword) {
    return (
      <div className="w-full flex flex-col gap-3">
        <div className="flex items-start justify-between gap-2 mb-2">
          <div>
            <h3 className="text-lg font-semibold text-white">{t("key57")}</h3>
            <p className="text-sm text-white/70 leading-relaxed mt-1">
              {t("key66")}
            </p>
          </div>
          <button
            type="button"
            onClick={() => setShowForgotPassword(false)}
            className="p-2 rounded-full bg-white/10 border border-white/15 text-white hover:bg-white/20 transition"
          >
            <HiOutlineArrowLeft className="w-5 h-5" />
          </button>
        </div>

        <LmcUserViewForgotPassword />

        {onBack && (
          <button
            onClick={() => {
              setShowForgotPassword(false);
              onBack();
            }}
            className="w-full px-4 py-2 text-gray-400 hover:text-white transition-colors text-sm mt-2"
          >
            {t("key26")}
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="w-full flex flex-col gap-3">
      <div className="flex items-center justify-center mb-2">
        <span className="text-white font-medium">{t("key25")}</span>
      </div>

      <div className="w-full flex-col justify-between items-center flex gap-2">
        <SocialAuth
          className="w-full"
          providers={["Google", "Apple"]}
          onLoginStartSocialAuthStart={() => {}}
          onResolveSocialAuth={handleResolveSocialAuth}
          onRejectSocialAuth={handleRejectSocialAuth}
          redirectUrl={{
            Apple: `${process.env.REACT_APP_APPLE_CALLBACK}`,
          }}
          providersIcon={{
            Apple: (
              <div className="bg-gray-700 hover:bg-gray-600 rounded-xl px-4 w-full h-11 flex flex-row justify-center items-center cursor-pointer relative border border-gray-600 transition-all duration-200 shadow-sm">
                <img
                  src="../../images/apple.svg"
                  alt=""
                  className="w-5 h-5 absolute left-4"
                />
                <p className="text-white text-sm font-semibold font-figtree leading-normal">
                  {t("key18")}
                </p>
              </div>
            ),
            Google: (
              <div className="bg-gray-700 hover:bg-gray-600 rounded-xl px-4 w-full h-11 flex flex-row justify-center items-center cursor-pointer relative border border-gray-600 transition-all duration-200 shadow-sm">
                <img
                  src="../../images/google.svg"
                  alt=""
                  className="w-5 h-5 absolute left-4"
                />
                <p className="text-white text-sm font-semibold font-figtree leading-normal">
                  {t("key19")}
                </p>
              </div>
            ),
          }}
        />
      </div>

      <div className="flex items-center gap-4">
        <div className="flex-1 h-px bg-white/20"></div>
        <p className="text-center text-gray-400 text-xs font-medium font-figtree uppercase leading-none">
          {t("key20")}
        </p>
        <div className="flex-1 h-px bg-white/20"></div>
      </div>

      <LoyaltyEmailLogin
        onLoginSuccess={onLoginSuccess}
        onForgotPassword={() => setShowForgotPassword(true)}
      />

      {onBack && (
        <button
          onClick={onBack}
          className="w-full px-4 py-2 text-gray-400 hover:text-white transition-colors text-sm mt-2"
        >
          {t("key26")}
        </button>
      )}
    </div>
  );
};

export default LoyaltyLoginModal;
